// Pure helpers for bulk image generation (GenerateAllImagesModal).
// Extracted so counting, eligibility and job-activity checks can be unit tested
// and so the "Generate Missing"/"Overwrite" counts always match the elements
// that actually get queued.

export const ACTIVE_JOB_STATUSES = ['pending', 'received', 'retrying'];

// Element ids are not uniform across the codebase: most lookups use
// element.id || element.docId, and bulk jobs store elementId the same way.
export const canonicalElementId = (element, elementId) =>
  element?.id || element?.docId || elementId;

export const elementHasPrompts = (element) =>
  (Array.isArray(element?.imagePrompts) && element.imagePrompts.length > 0) ||
  !!element?.imagePrompt;

export const elementHasUsableImage = (element) =>
  !!element?.src && element.src !== '';

// Set of `${spreadId}:${elementId}` keys for jobs that are currently in flight.
export const buildPendingJobKeySet = (pendingImageJobs) => {
  const set = new Set();
  if (pendingImageJobs && pendingImageJobs.size > 0) {
    for (const job of pendingImageJobs.values()) {
      if (job?.spreadId && job?.elementId) {
        set.add(`${job.spreadId}:${job.elementId}`);
      }
    }
  }
  return set;
};

// Whether an element already has an in-flight generation job.
// Once pendingImageJobsLoaded is true, the live job map is authoritative:
// element-side flags (imageGenerationStatus / imageUpdateJobId) can be left
// behind by failed, cancelled or orphaned jobs and must not block regeneration.
export const hasActiveJob = ({
  element,
  spreadId,
  elementId,
  pendingJobKeySet,
  pendingImageJobs,
  pendingImageJobsLoaded,
}) => {
  const canonicalId = canonicalElementId(element, elementId);

  if (pendingImageJobsLoaded) {
    if (spreadId && canonicalId && pendingJobKeySet?.has(`${spreadId}:${canonicalId}`)) return true;
    if (element?.imageUpdateJobId && pendingImageJobs?.get(element.imageUpdateJobId)) return true;
    return false;
  }

  // Before the job snapshot arrives, fall back to element flags so we don't
  // double-queue elements whose jobs were just created.
  if (element?.imageGenerationStatus === 'pending' || !!element?.imageUpdateJobId) return true;
  if (spreadId && canonicalId && pendingJobKeySet?.has(`${spreadId}:${canonicalId}`)) return true;
  return false;
};

// Overwrite mode: image elements that already have a usable image and prompts,
// and are not currently generating.
export const isOverwritableImage = ({ element, spreadId, elementId, ...jobCtx }) => {
  if (element?.type !== 'image') return false;
  if (!elementHasPrompts(element)) return false;
  if (hasActiveJob({ element, spreadId, elementId, ...jobCtx })) return false;
  if (!elementHasUsableImage(element)) return false;
  return Array.isArray(element.images) && element.images.length > 0;
};

const iterElements = (spreads, fn) => {
  Object.entries(spreads || {}).forEach(([spreadId, spread]) => {
    // Object.entries handles both array elements (index keys) and map elements (id keys)
    Object.entries(spread?.elements || {}).forEach(([elementKey, element]) => {
      fn(spreadId, elementKey, element);
    });
  });
};

// Elements eligible for "Generate Missing": image elements with no usable src
// and no in-flight job. Failed generations land here because the backend clears
// imageGenerationStatus/imageUpdateJobId and leaves src empty.
export const collectMissingImageElements = (spreads, jobCtx) => {
  const result = [];
  iterElements(spreads, (spreadId, elementKey, element) => {
    if (element?.type !== 'image') return;
    const elementId = canonicalElementId(element, elementKey);
    if (!elementHasUsableImage(element) && !hasActiveJob({ element, spreadId, elementId, ...jobCtx })) {
      result.push({ ...element, spreadId, elementKey, elementId });
    }
  });
  return result;
};

// Elements eligible for "Overwrite Existing".
export const collectOverwritableImageElements = (spreads, jobCtx) => {
  const result = [];
  iterElements(spreads, (spreadId, elementKey, element) => {
    const elementId = canonicalElementId(element, elementKey);
    if (isOverwritableImage({ element, spreadId, elementId, ...jobCtx })) {
      result.push({ ...element, spreadId, elementKey, elementId });
    }
  });
  return result;
};

// Counts used by the modal UI. Returns element lists so callers can also use
// them for queueing — keeps the displayed counts consistent with what runs.
export const calculateMissingElements = (book, jobCtx) => {
  const allElements = [];
  const missingElements = [];
  const elementsWithoutPrompts = [];
  const overwriteElements = [];

  iterElements(book?.spreads, (spreadId, elementKey, element) => {
    if (element?.type !== 'image') return;
    const elementId = canonicalElementId(element, elementKey);
    const hasPrompts = elementHasPrompts(element);
    const isMissing = !elementHasUsableImage(element);

    if (hasPrompts) {
      allElements.push({ ...element, spreadId, elementId });
      if (isOverwritableImage({ element, spreadId, elementId, ...jobCtx })) {
        overwriteElements.push({ ...element, spreadId, elementId });
      }
    }

    // Missing images: any image without a src and no in-flight job, prompt or not
    if (isMissing && !hasActiveJob({ element, spreadId, elementId, ...jobCtx })) {
      missingElements.push({ ...element, spreadId, elementId });
    }

    // Prompt-less images that already have an image (don't double-count missing ones)
    if (!hasPrompts && !isMissing) {
      elementsWithoutPrompts.push({ ...element, spreadId, elementId });
    }
  });

  return { allElements, missingElements, elementsWithoutPrompts, overwriteElements };
};
